import { HIDDEN_TABLE_DETAIL_TAB_IDS, SPACE_TABS, TABLE_DETAIL_TABS } from './dataModelsConstants';
import { logDataModels } from './dataModelsLogger';
import type { CatalogTabId, SpaceTabId, TableDetailTabId } from './dataModelsTypes';
import type { PolicyEditorView } from './governance/policyEditorNav';

export interface DataModelsUiSnapshot {
  railOpen: boolean;
  selectedSpaceId: string | null;
  selectedModelId: string | null;
  selectedTableId: string | null;
  expandedSpaceIds: string[];
  expandedModelIds: string[];
  spaceOrderIds: string[];
  spaceTab: SpaceTabId;
  catalogTab: CatalogTabId;
  tableTab: TableDetailTabId;
}

export interface HydratedDataModelsUi {
  snapshot: DataModelsUiSnapshot;
  restored: boolean;
}

const STORAGE_PREFIX = 'selficai.data-models.ui.v1';

const SPACE_TAB_IDS = new Set<string>([...SPACE_TABS.map((tab) => tab.id), 'sources']);
const CATALOG_TAB_IDS = new Set<string>(['overview', 'assets', 'policies', 'access', 'activity', 'queries']);
const TABLE_TAB_IDS = new Set<string>(TABLE_DETAIL_TABS.map((tab) => tab.id));

export const DEFAULT_DATA_MODELS_UI: DataModelsUiSnapshot = {
  railOpen: true,
  selectedSpaceId: null,
  selectedModelId: null,
  selectedTableId: null,
  expandedSpaceIds: [],
  expandedModelIds: [],
  spaceOrderIds: [],
  spaceTab: 'overview',
  catalogTab: 'overview',
  tableTab: 'overview',
};

function storageKey(): string {
  const tenantId = typeof window === 'undefined' ? '' : window.localStorage.getItem('tenant_id')?.trim();
  return `${STORAGE_PREFIX}:${tenantId || 'anon'}`;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function asTab<T extends string>(value: unknown, allowed: ReadonlySet<string>, fallback: T): T {
  return typeof value === 'string' && allowed.has(value) ? (value as T) : fallback;
}

function visibleTableTab(tab: TableDetailTabId): TableDetailTabId {
  return HIDDEN_TABLE_DETAIL_TAB_IDS.has(tab) ? DEFAULT_DATA_MODELS_UI.tableTab : tab;
}

function parseSnapshot(raw: string): DataModelsUiSnapshot {
  const parsed: unknown = JSON.parse(raw);
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('ui snapshot is not an object');
  }
  const value = parsed as Record<string, unknown>;
  return {
    railOpen: asBoolean(value.railOpen, DEFAULT_DATA_MODELS_UI.railOpen),
    selectedSpaceId: asNullableString(value.selectedSpaceId),
    selectedModelId: asNullableString(value.selectedModelId),
    selectedTableId: asNullableString(value.selectedTableId),
    expandedSpaceIds: asStringArray(value.expandedSpaceIds),
    expandedModelIds: asStringArray(value.expandedModelIds),
    spaceOrderIds: asStringArray(value.spaceOrderIds),
    spaceTab: asTab<SpaceTabId>(value.spaceTab, SPACE_TAB_IDS, DEFAULT_DATA_MODELS_UI.spaceTab),
    catalogTab: asTab<CatalogTabId>(value.catalogTab, CATALOG_TAB_IDS, DEFAULT_DATA_MODELS_UI.catalogTab),
    tableTab: visibleTableTab(asTab<TableDetailTabId>(value.tableTab, TABLE_TAB_IDS, DEFAULT_DATA_MODELS_UI.tableTab)),
  };
}

export function readDataModelsUiState(): HydratedDataModelsUi {
  if (typeof window === 'undefined') {
    return { snapshot: DEFAULT_DATA_MODELS_UI, restored: false };
  }
  try {
    const raw = window.sessionStorage.getItem(storageKey());
    if (!raw) return { snapshot: DEFAULT_DATA_MODELS_UI, restored: false };
    const snapshot = parseSnapshot(raw);
    logDataModels('app', 'ui_state_restored', {
      railOpen: snapshot.railOpen,
      selectedSpaceId: snapshot.selectedSpaceId,
      selectedModelId: snapshot.selectedModelId,
      selectedTableId: snapshot.selectedTableId,
      spaceTab: snapshot.spaceTab,
      catalogTab: snapshot.catalogTab,
      tableTab: snapshot.tableTab,
      expandedSpaces: snapshot.expandedSpaceIds.length,
      expandedModels: snapshot.expandedModelIds.length,
      spaceOrder: snapshot.spaceOrderIds.length,
    });
    return { snapshot, restored: true };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unable to read WorkSpace UI state';
    logDataModels('warning', 'ui_state_restore_failed', { message });
    return { snapshot: DEFAULT_DATA_MODELS_UI, restored: false };
  }
}

export function writeDataModelsUiState(snapshot: DataModelsUiSnapshot): void {
  if (typeof window === 'undefined') return;
  try {
    const next = { ...snapshot, tableTab: visibleTableTab(snapshot.tableTab) };
    window.sessionStorage.setItem(storageKey(), JSON.stringify(next));
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unable to persist WorkSpace UI state';
    logDataModels('warning', 'ui_state_persist_failed', { message });
  }
}

export function hydrateDataModelsUiState(policy: PolicyEditorView | null): HydratedDataModelsUi {
  const { snapshot, restored } = readDataModelsUiState();
  if (!policy) return { snapshot, restored };
  const expandedSpaceIds = [...snapshot.expandedSpaceIds];
  const expandedModelIds = [...snapshot.expandedModelIds];
  if (policy.spaceId && !expandedSpaceIds.includes(policy.spaceId)) expandedSpaceIds.push(policy.spaceId);
  if (policy.modelId && !expandedModelIds.includes(policy.modelId)) expandedModelIds.push(policy.modelId);
  const next: DataModelsUiSnapshot = {
    ...snapshot,
    expandedSpaceIds,
    expandedModelIds,
    selectedSpaceId: policy.spaceId ?? snapshot.selectedSpaceId,
  };
  if (policy.level === 'asset') {
    next.selectedTableId = policy.tableId ?? null;
    next.selectedModelId = null;
  } else if (policy.level === 'model') {
    next.selectedTableId = null;
    next.selectedModelId = policy.modelId ?? null;
  } else {
    next.selectedTableId = null;
    next.selectedModelId = null;
    next.spaceTab = 'governance';
  }
  return { snapshot: next, restored };
}
